import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowDown, ArrowUp, Download, Hash, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, STATUS_CATEGORIES, type ProjectRole, type StatusCategory } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { UserLink } from '@/components/shared/UserLink';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { useCan } from '@/features/auth';
import { useUsers } from '@/features/settings/api';
import { useNotes } from '@/features/notes/api';
import { useProjectChannel } from '@/features/chat/api';
import {
  useArchiveProject,
  useDeleteProject,
  useExportProject,
  useProject,
  useProjectMembers,
  useProjectStats,
  useReplaceStatuses,
  useUpdateProject,
  type ProjectStatusView,
} from './api';

const PROJECT_ROLES: ProjectRole[] = ['lead', 'contributor', 'reviewer', 'viewer'];

function StatusEditor({ projectId, statuses }: { projectId: string; statuses: ProjectStatusView[] }) {
  const replace = useReplaceStatuses();
  const [draft, setDraft] = useState<ProjectStatusView[]>(statuses);
  const [migrateTo, setMigrateTo] = useState<string | undefined>();

  useEffect(() => setDraft(statuses), [statuses]);

  function move(index: number, delta: number) {
    const next = [...draft];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next.map((s, i) => ({ ...s, order: i })));
  }

  function patch(index: number, changes: Partial<ProjectStatusView>) {
    setDraft((prev) => prev.map((s, i) => (i === index ? { ...s, ...changes } : s)));
  }

  async function save() {
    try {
      await replace.mutateAsync({ id: projectId, statuses: draft, migrateTo });
      setMigrateTo(undefined);
      toast.success('Pipeline saved');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'STATUS_HAS_TASKS') {
        toast.error(err.message, {
          description: 'Pick a status to move those tasks into, then save again.',
        });
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Could not save the pipeline');
      }
    }
  }

  const removedCount = statuses.length - draft.filter((d) => statuses.some((s) => s.id === d.id)).length;

  return (
    <div className="vt-surface flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold">Statuses</h2>
          <p className="text-[11px] vt-muted">
            Every status maps to a category so reports work across projects.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            setDraft((prev) => [
              ...prev,
              {
                id: `new_${prev.length}_${Date.now()}`,
                name: 'New status',
                category: 'todo',
                colour: '#7C838A',
                order: prev.length,
                wipLimit: null,
              },
            ])
          }
        >
          <Plus />
          Add status
        </Button>
      </div>

      <ul className="flex flex-col gap-1.5">
        {draft.map((status, index) => (
          <li key={status.id} className="flex flex-wrap items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: status.colour }} aria-hidden />
            <Input
              className="w-[150px]"
              value={status.name}
              aria-label={`Status ${index + 1} name`}
              onChange={(e) => patch(index, { name: e.target.value })}
            />
            <select
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              value={status.category}
              aria-label={`Status ${index + 1} category`}
              onChange={(e) => patch(index, { category: e.target.value as StatusCategory })}
            >
              {STATUS_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replace('_', ' ')}
                </option>
              ))}
            </select>
            <Input
              type="number"
              min={1}
              className="w-[84px]"
              placeholder="WIP"
              aria-label={`Status ${index + 1} WIP limit`}
              value={status.wipLimit ?? ''}
              onChange={(e) => patch(index, { wipLimit: e.target.value ? Number(e.target.value) : null })}
            />
            <div className="ml-auto flex gap-0.5">
              <Button variant="ghost" size="icon-sm" aria-label="Move up" onClick={() => move(index, -1)}>
                <ArrowUp />
              </Button>
              <Button variant="ghost" size="icon-sm" aria-label="Move down" onClick={() => move(index, 1)}>
                <ArrowDown />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${status.name}`}
                className="text-[var(--danger)]"
                onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {removedCount > 0 && (
        <div className="flex items-center gap-2 rounded-[6px] bg-[var(--warn)]/10 p-2 text-[12px]">
          <span>Move any tasks in removed statuses to:</span>
          <select
            className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[12px]"
            value={migrateTo ?? ''}
            onChange={(e) => setMigrateTo(e.target.value || undefined)}
          >
            <option value="">Choose…</option>
            {draft.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button variant="primary" onClick={save} disabled={replace.isPending}>
          {replace.isPending ? <Loader2 className="animate-spin" /> : null}
          Save pipeline
        </Button>
      </div>
    </div>
  );
}

/** The three levels, in the words a lead needs to choose between them. */
const VISIBILITY_LEVELS = [
  {
    value: 'private' as const,
    title: 'Only project members',
    hint: 'Members, plus everyone in a team linked to this project below. Owners and admins can always see it.',
  },
  {
    value: 'workspace' as const,
    title: 'Everyone in the company',
    hint: 'Anyone with an account can find and open it. Good for shared, non-sensitive work.',
  },
  {
    value: 'guest_shared' as const,
    title: 'Members and invited guests',
    hint: 'As above, plus named external guests — read-only, and only this project.',
  },
];

export function ProjectSettingsPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const projectChannel = useProjectChannel();
  const can = useCan();
  const { data: project, isLoading } = useProject(key);
  const { data: people = [] } = useUsers();
  const update = useUpdateProject();
  const archive = useArchiveProject();
  const destroy = useDeleteProject();
  const exportProject = useExportProject();
  const { data: stats } = useProjectStats(project?._id);
  // Counted for the delete warning only.
  const { data: projectNotes = [] } = useNotes({ projectId: project?._id });
  const members = useProjectMembers();

  const [confirmKey, setConfirmKey] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isLoading || !project) {
    return (
      <>
        <PageHeader title="Project settings" />
        <div className="max-w-[720px] p-4">
          <Skeleton className="h-72" />
        </div>
      </>
    );
  }

  const byId = new Map(people.map((p) => [p._id, p]));
  const canEdit = can(PERMISSIONS.PROJECT_UPDATE);

  async function patchSettings(patch: Record<string, unknown>) {
    try {
      await update.mutateAsync({ id: project!._id, patch });
      toast.success('Project updated');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update the project');
    }
  }

  return (
    <>
      <PageHeader
        title={`${project.icon} ${project.name}`}
        subtitle={`Key ${project.key} · ${project.statuses.length} statuses`}
      />

      <div className="flex max-w-[760px] flex-col gap-3 p-4">
        {/**
         * V7-04 §1 — one channel per project, on demand.
         *
         * Not created with the project: most projects never need one, and a
         * workspace whose channel directory is one dead room per project is a
         * directory nobody browses. Task activity deliberately does not post
         * there [DECIDED, §1] — that noise belongs on the task.
         */}
        <section className="vt-surface flex flex-col gap-2 p-4" data-testid="project-channel">
          <h2 className="text-[15px] font-semibold">Chat channel</h2>
          <p className="text-[12px] vt-muted">
            A channel named for this project, with its members already in it. Task activity does not
            post there — sharing a task into it is something you choose to do.
          </p>
          <div>
            <Button
              variant="secondary"
              size="sm"
              data-testid="project-channel-open"
              disabled={!canEdit || projectChannel.isPending}
              onClick={() =>
                projectChannel.mutate(project._id, {
                  onSuccess: (conversation) => navigate(`/chat?c=${conversation._id}`),
                  onError: (error) =>
                    toast.error(
                      error instanceof ApiError ? error.message : 'Could not open the channel',
                    ),
                })
              }
            >
              {projectChannel.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Hash className="size-3.5" />
              )}
              Open the project channel
            </Button>
          </div>
        </section>

        <section className="vt-surface flex flex-col gap-3 p-4">
          <h2 className="text-[15px] font-semibold">Behaviour</h2>

          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!canEdit}
              checked={project.settings.qaEnabled}
              onChange={(e) => void patchSettings({ settings: { qaEnabled: e.target.checked } })}
            />
            <span>
              QA column
              <span className="block text-[11px] vt-muted">
                Adds “In QA” between In Review and Blocked. Turning it off needs that column empty.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!canEdit}
              checked={project.settings.wipEnforced}
              onChange={(e) => void patchSettings({ settings: { wipEnforced: e.target.checked } })}
            />
            <span>
              Enforce WIP limits
              <span className="block text-[11px] vt-muted">
                Off: a full column warns. On: the move is rejected for everyone, admins included.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!canEdit}
              checked={project.settings.timeTrackingRequired}
              onChange={(e) => void patchSettings({ settings: { timeTrackingRequired: e.target.checked } })}
            />
            <span>
              Expect time logged before Done
              <span className="block text-[11px] vt-muted">
                A nudge with visibility on the dashboard, never a hard block.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!canEdit}
              checked={project.settings.requireReviewBeforeDone}
              onChange={(e) => void patchSettings({ settings: { requireReviewBeforeDone: e.target.checked } })}
            />
            <span>Require review before Done</span>
          </label>

          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!canEdit}
              checked={project.settings.allowGuestComments}
              onChange={(e) => void patchSettings({ settings: { allowGuestComments: e.target.checked } })}
            />
            <span>Allow guests to comment</span>
          </label>

        </section>

        {/* V4 D-1.5: who can see this project is the most consequential setting
            on the page, so it gets its own card and says what each level
            actually means rather than making people infer it from three
            words in a dropdown. */}
        <section className="vt-surface flex flex-col gap-2 p-4" data-testid="project-visibility">
          <h2 className="text-[15px] font-semibold">Who can see this project</h2>
          <p className="text-[12px] vt-muted">
            Everyone who can see a project sees all of its tasks. Focus comes from My Day and My
            Tasks, not from hiding work — if something genuinely must be kept apart, make it a
            separate project.
          </p>

          <div className="mt-1 flex flex-col gap-2">
            {VISIBILITY_LEVELS.map((level) => (
              <label
                key={level.value}
                className={`flex gap-2.5 rounded-[8px] border p-2.5 transition-colors duration-[120ms] ${
                  project.visibility === level.value
                    ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
                    : 'border-[var(--border)]'
                } ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
              >
                <input
                  type="radio"
                  name="project-visibility"
                  className="mt-0.5"
                  value={level.value}
                  disabled={!canEdit}
                  checked={project.visibility === level.value}
                  onChange={() => void patchSettings({ visibility: level.value })}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-[13px] font-medium">{level.title}</span>
                  <span className="text-[11px] vt-muted">{level.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        {can(PERMISSIONS.PROJECT_MANAGE_STATUSES) && (
          <StatusEditor projectId={project._id} statuses={project.statuses} />
        )}

        <section className="vt-surface flex flex-col gap-2 p-4">
          <h2 className="text-[15px] font-semibold">Members</h2>
          <ul className="flex flex-col gap-1.5">
            {project.members.map((m) => {
              const person = byId.get(m.userId);
              return (
                <li key={m.userId} className="flex items-center gap-2 text-[13px]">
                  <UserLink
                    userId={m.userId}
                    name={person?.name ?? 'Unknown'}
                    avatarUrl={person?.avatarUrl}
                    size={22}
                    className="flex-1 truncate"
                  />
                  <select
                    className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[12px]"
                    value={m.projectRole}
                    disabled={!can(PERMISSIONS.PROJECT_MANAGE_MEMBERS)}
                    aria-label={`Project role for ${person?.name ?? m.userId}`}
                    onChange={(e) => {
                      void members.update
                        .mutateAsync({
                          id: project._id,
                          userId: m.userId,
                          projectRole: e.target.value as ProjectRole,
                        })
                        .catch((err: unknown) =>
                          toast.error(err instanceof ApiError ? err.message : 'Could not change the role'),
                        );
                    }}
                  >
                    {PROJECT_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {can(PERMISSIONS.PROJECT_MANAGE_MEMBERS) && m.userId !== project.leadId && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${person?.name ?? 'member'}`}
                      className="text-[var(--danger)]"
                      onClick={() => {
                        void members.remove
                          .mutateAsync({ id: project._id, userId: m.userId })
                          .catch((err: unknown) =>
                            toast.error(err instanceof ApiError ? err.message : 'Could not remove'),
                          );
                      }}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>

          {can(PERMISSIONS.PROJECT_MANAGE_MEMBERS) && (
            <select
              className="mt-1 h-8 w-[220px] rounded-[6px] border border-dashed border-[var(--border)] bg-transparent px-2 text-[12px] vt-muted"
              value=""
              aria-label="Add a project member"
              onChange={(e) => {
                if (!e.target.value) return;
                void members.add
                  .mutateAsync({ id: project._id, userId: e.target.value, projectRole: 'contributor' })
                  .catch((err: unknown) =>
                    toast.error(err instanceof ApiError ? err.message : 'Could not add'),
                  );
              }}
            >
              <option value="">+ Add member</option>
              {people
                .filter((p) => p.status === 'active' && !project.members.some((m) => m.userId === p._id))
                .map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
            </select>
          )}
        </section>

        {can(PERMISSIONS.PROJECT_ARCHIVE) && (
          <section className="vt-surface flex flex-col gap-3 p-4">
            <h2 className="text-[15px] font-semibold">Lifecycle</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  void archive
                    .mutateAsync({ id: project._id, archived: project.status !== 'archived' })
                    .then(() =>
                      toast.success(project.status === 'archived' ? 'Project restored' : 'Project archived'),
                    )
                    .catch((err: unknown) =>
                      toast.error(err instanceof ApiError ? err.message : 'Could not change the status'),
                    );
                }}
              >
                {project.status === 'archived' ? 'Restore project' : 'Archive project'}
              </Button>
              <span className="text-[11px] vt-muted">
                Archiving makes the project and every task in it read-only. Restoring is lossless.
              </span>
            </div>

            {/* V4 F-3 — a copy of everything, before anyone deletes anything. */}
            <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
              <Button
                variant="subtle"
                data-testid="export-project"
                disabled={exportProject.isPending}
                onClick={() => {
                  void exportProject
                    .mutateAsync(project._id)
                    .then((r) => toast.success(r.message))
                    .catch((err: unknown) =>
                      toast.error(err instanceof ApiError ? err.message : 'Could not start the export'),
                    );
                }}
              >
                {exportProject.isPending ? <Loader2 className="animate-spin" /> : <Download />}
                Export project data
              </Button>
              <span className="text-[11px] vt-muted">
                Tasks, comments, notes, time and a file manifest, as a zip. The link arrives as a
                notification and lasts 24 hours.
              </span>
            </div>

            {can(PERMISSIONS.PROJECT_DELETE) && (
              <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
                <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                  Delete project
                </Button>
                <span className="text-[11px] vt-muted">
                  Soft delete — tasks are recoverable for 30 days, then purged.
                </span>
              </div>
            )}
          </section>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogTitle className="text-[16px] font-semibold">Delete {project.name}?</DialogTitle>
          <p className="mb-2 mt-1 text-[13px] vt-muted">
            This soft-deletes the project and everything in it. It is restorable for 30 days from
            Settings → Workspace, then purged for good.
          </p>

          {/* Say what is actually being deleted. "Delete project?" is not a
              decision anyone can make; "and its 140 tasks" is. */}
          <ul className="mb-3 flex flex-col gap-0.5 text-[13px]" data-testid="delete-counts">
            <li>
              <strong className="vt-mono">{stats?.totalTasks ?? 0}</strong> tasks
            </li>
            <li>
              <strong className="vt-mono">{projectNotes.length}</strong> notes
            </li>
          </ul>

          <div className="mb-3">
            <Button
              variant="subtle"
              size="sm"
              data-testid="export-before-delete"
              disabled={exportProject.isPending}
              onClick={() => {
                void exportProject
                  .mutateAsync(project._id)
                  .then((r) => toast.success(r.message))
                  .catch(() => toast.error('Could not start the export'));
              }}
            >
              <Download />
              Export it first
            </Button>
          </div>

          <p className="mb-1.5 text-[13px] vt-muted">
            Type <strong>{project.key}</strong> to confirm.
          </p>
          <Input
            value={confirmKey}
            autoFocus
            className="vt-mono uppercase"
            aria-label="Type the project key to confirm"
            onChange={(e) => setConfirmKey(e.target.value.toUpperCase())}
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={confirmKey !== project.key || destroy.isPending}
              onClick={() => {
                void destroy
                  .mutateAsync({ id: project._id, confirmKey })
                  .then((result) => {
                    toast.success(`Project deleted — ${result.tasksSoftDeleted} tasks archived with it`);
                    navigate('/projects');
                  })
                  .catch((err: unknown) =>
                    toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
                  );
              }}
            >
              {destroy.isPending ? <Loader2 className="animate-spin" /> : null}
              Delete permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
